/**
 * MODULE: domain/workspace/frontend/api/workspaceApi.ts
 * PURPOSE: API client for workspace endpoints
 * 
 * DEPENDENCIES:
 *   - ../../contracts/workspace.types: TypeScript type definitions
 *   - @shared/lib/frontend/api/errors: getErrorMessage
 * 
 * PUBLIC API:
 *   - workspaceApi.list(): Fetch paginated workspaces list
 *   - workspaceApi.get(id): Fetch single workspace
 *   - workspaceApi.create(data): Create workspace
 *   - workspaceApi.update(id, data): Update workspace
 *   - workspaceApi.delete(id): Delete workspace
 *   - workspaceApi.listMembers(workspaceId): List workspace members
 *   - workspaceApi.addMember(workspaceId, data): Add member to workspace
 *   - workspaceApi.removeMember(workspaceId, memberId): Remove member from workspace
 * 
 * SIDE EFFECTS:
 *   - HTTP requests to backend API
 *   - Reads access_token from localStorage
 * 
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: 115 / 150 lines
 * 
 * MIGRATION NOTE (Jan 2026):
 *   Workspaces merged into tenant-service. API Gateway handles routing:
 *   - /api/v1/workspaces -> /api/v1/tenants/{tenant_id}/workspaces
 *   - Tenant ID extracted from JWT token automatically
 *   - This client continues to work with /api/v1/workspaces for backwards compatibility
 * 
 * Recent: 2026-05-24 — VITE_USE_MOCK_DATA=false honored; VITE_PAGES_E2E_STUB=1 uses fetch for listMembers. 2026-05-24 [2.2.1137] — MOCK_WORKSPACE_IDS UUIDs; listMembers mock fallback (Pages share UI). 2026-03-03 [2.2.161] - Mock workspaces tenant_id, slug, visibility, owner_id, member_count; WorkspaceListResponse page/page_size/total_pages. 2026-02-24 - getErrorMessage.
 * See: /README.md#documentation | /ARCHITECTURE.md | /domain/workspace/README.md
 */

import { getErrorMessage } from '@shared/lib/frontend/api/errors';
import type {
  Workspace,
  WorkspaceMember,
  WorkspaceMemberRole,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  AddMemberRequest,
  WorkspaceFilters,
  WorkspaceListResponse,
} from '../../contracts/workspace.types';

// API Gateway routes /api/v1/workspaces to tenant-service automatically
// Tenant ID is extracted from JWT token by the gateway
const API_BASE = '/api/v1/workspaces';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(getErrorMessage(error, `HTTP ${response.status}`));
  }
  return response.json();
}

function buildQueryString(filters?: WorkspaceFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

const MOCK_WORKSPACES_KEY = 'brazn-mock-workspaces';
const MOCK_WORKSPACES_VERSION_KEY = 'brazn-mock-workspaces-version';
/** Bump when mock workspace shape changes (2026-05-24: slug ids → UUID for pages-service). */
const MOCK_WORKSPACES_VERSION = 'v3-2026-05-24-uuid-ids';

/** Stable UUIDs for UAT mock workspaces (pages-service requires UUID workspace_id). */
export const MOCK_WORKSPACE_IDS = {
  acmeSalesHq: 'a1000001-0001-4000-8000-000000000001',
  enterpriseAccounts: 'a1000001-0002-4000-8000-000000000002',
  smbPipeline: 'a1000001-0003-4000-8000-000000000003',
} as const;

function newMockWorkspaceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'a1000001-0004-4000-8000-' + String(Date.now()).padStart(12, '0').slice(-12);
}

function shouldUseMockData(): boolean {
  const mockFlag =
    typeof import.meta !== 'undefined'
      ? (import.meta as { env?: { VITE_USE_MOCK_DATA?: string } }).env?.VITE_USE_MOCK_DATA
      : undefined;
  if (mockFlag === 'false') return false;
  if (mockFlag === 'true') return true;
  try {
    const tenantId = localStorage.getItem('tenant_id') || '';
    const tenantName = localStorage.getItem('tenant_name') || '';
    if (tenantId.toLowerCase().includes('acme') || tenantName.toLowerCase().includes('acme')) {
      return true;
    }
  } catch {
    // ignore
  }
  return true; // Default to mock data for UAT/demo
}

function getMockWorkspaces(): Workspace[] {
  try {
    const storedVersion = localStorage.getItem(MOCK_WORKSPACES_VERSION_KEY);
    const stored = localStorage.getItem(MOCK_WORKSPACES_KEY);
    if (storedVersion === MOCK_WORKSPACES_VERSION && stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  const defaultWorkspaces: Workspace[] = [
    {
      id: MOCK_WORKSPACE_IDS.acmeSalesHq,
      name: 'Acme Sales HQ',
      description: 'Primary sales workspace for the Acme Corp team',
      slug: 'acme-sales-hq',
      tenant_id: 'mock-tenant',
      status: 'active',
      visibility: 'team',
      owner_id: 'mock-owner',
      member_count: 0,
      settings: { default_pipeline: 'standard', notifications_enabled: true },
      created_at: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: MOCK_WORKSPACE_IDS.enterpriseAccounts,
      name: 'Enterprise Accounts',
      description: 'Dedicated workspace for enterprise-tier customers',
      slug: 'enterprise-accounts',
      tenant_id: 'mock-tenant',
      status: 'active',
      visibility: 'team',
      owner_id: 'mock-owner',
      member_count: 0,
      settings: { default_pipeline: 'enterprise', notifications_enabled: true },
      created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: MOCK_WORKSPACE_IDS.smbPipeline,
      name: 'SMB Pipeline',
      description: 'Small and medium business opportunities',
      slug: 'smb-pipeline',
      tenant_id: 'mock-tenant',
      status: 'active',
      visibility: 'team',
      owner_id: 'mock-owner',
      member_count: 0,
      settings: { default_pipeline: 'standard', notifications_enabled: true },
      created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ];
  localStorage.setItem(MOCK_WORKSPACES_KEY, JSON.stringify(defaultWorkspaces));
  localStorage.setItem(MOCK_WORKSPACES_VERSION_KEY, MOCK_WORKSPACES_VERSION);
  return defaultWorkspaces;
}

function saveMockWorkspaces(workspaces: Workspace[]): void {
  try {
    localStorage.setItem(MOCK_WORKSPACES_KEY, JSON.stringify(workspaces));
  } catch {
    // ignore
  }
}

function isMockWorkspaceId(workspaceId: string): boolean {
  const mockIds = Object.values(MOCK_WORKSPACE_IDS) as string[];
  if (mockIds.includes(workspaceId)) return true;
  return getMockWorkspaces().some((w) => w.id === workspaceId);
}

function shouldUsePagesE2EStubFetch(): boolean {
  return (
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { VITE_PAGES_E2E_STUB?: string } }).env?.VITE_PAGES_E2E_STUB === '1'
  );
}

function shouldUseMockMembers(workspaceId: string): boolean {
  if (shouldUsePagesE2EStubFetch()) {
    return false;
  }
  return shouldUseMockData() || isMockWorkspaceId(workspaceId);
}

function getMockWorkspaceMembers(workspaceId: string): WorkspaceMember[] {
  const workspace = getMockWorkspaces().find((w) => w.id === workspaceId);
  if (!workspace) return [];
  return [
    {
      id: 'mock-member-owner',
      workspace_id: workspaceId,
      user_id: workspace.owner_id,
      role: 'owner',
      joined_at: workspace.created_at,
      user: {
        id: workspace.owner_id,
        name: 'Demo Owner',
        email: 'owner@acme.example',
      },
    },
    {
      id: 'mock-member-2',
      workspace_id: workspaceId,
      user_id: 'mock-member-user-2',
      role: 'member',
      joined_at: workspace.created_at,
      user: {
        id: 'mock-member-user-2',
        name: 'Alex Rep',
        email: 'alex@acme.example',
      },
    },
  ];
}

export const workspaceApi = {
  async list(filters?: WorkspaceFilters): Promise<WorkspaceListResponse> {
    const getMockList = (): WorkspaceListResponse => {
      let workspaces = getMockWorkspaces();
      if (filters?.status) {
        workspaces = workspaces.filter((w) => w.status === filters.status);
      }
      if (filters?.search) {
        const search = filters.search.toLowerCase();
        workspaces = workspaces.filter(
          (w) =>
            w.name.toLowerCase().includes(search) ||
            (w.description && w.description.toLowerCase().includes(search))
        );
      }
      return {
        data: workspaces,
        total: workspaces.length,
        page: 1,
        page_size: workspaces.length,
        total_pages: 1,
      };
    };

    if (shouldUseMockData()) {
      return getMockList();
    }

    try {
      const response = await fetch(`${API_BASE}${buildQueryString(filters)}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return handleResponse<WorkspaceListResponse>(response);
    } catch (err) {
      console.warn('Workspaces API unavailable, using mock data:', err);
      return getMockList();
    }
  },

  async get(id: string): Promise<Workspace> {
    const getMockWorkspace = (): Workspace => {
      const workspaces = getMockWorkspaces();
      const found = workspaces.find((w) => w.id === id);
      if (found) return found;
      throw new Error(`Workspace ${id} not found`);
    };

    if (shouldUseMockData()) {
      return getMockWorkspace();
    }

    try {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return handleResponse<Workspace>(response);
    } catch (err) {
      console.warn('Workspace get API unavailable, using mock data:', err);
      return getMockWorkspace();
    }
  },

  async create(data: CreateWorkspaceRequest): Promise<Workspace> {
    const createMockWorkspace = (): Workspace => {
      const workspaces = getMockWorkspaces();
      const newWorkspace: Workspace = {
        id: newMockWorkspaceId(),
        name: data.name,
        description: data.description,
        slug: data.name.toLowerCase().replace(/\s+/g, '-'),
        tenant_id: 'mock-tenant',
        status: 'active',
        visibility: 'team',
        owner_id: 'mock-owner',
        member_count: 0,
        settings: data.settings || {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      workspaces.push(newWorkspace);
      saveMockWorkspaces(workspaces);
      return newWorkspace;
    };

    if (shouldUseMockData()) {
      return createMockWorkspace();
    }

    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse<Workspace>(response);
    } catch (err) {
      console.warn('Workspace create API unavailable, using mock:', err);
      return createMockWorkspace();
    }
  },

  async update(id: string, data: UpdateWorkspaceRequest): Promise<Workspace> {
    const updateMockWorkspace = (): Workspace => {
      const workspaces = getMockWorkspaces();
      const index = workspaces.findIndex((w) => w.id === id);
      if (index >= 0) {
        workspaces[index] = { ...workspaces[index], ...data, updated_at: new Date().toISOString() };
        saveMockWorkspaces(workspaces);
        return workspaces[index];
      }
      throw new Error(`Workspace ${id} not found`);
    };

    if (shouldUseMockData()) {
      return updateMockWorkspace();
    }

    try {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse<Workspace>(response);
    } catch (err) {
      console.warn('Workspace update API unavailable, using mock:', err);
      return updateMockWorkspace();
    }
  },

  async delete(id: string): Promise<void> {
    const deleteMockWorkspace = (): void => {
      const workspaces = getMockWorkspaces();
      const filtered = workspaces.filter((w) => w.id !== id);
      saveMockWorkspaces(filtered);
    };

    if (shouldUseMockData()) {
      deleteMockWorkspace();
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Delete failed' }));
        throw new Error(getErrorMessage(error, 'Delete failed'));
      }
    } catch (err) {
      console.warn('Workspace delete API unavailable, using mock:', err);
      deleteMockWorkspace();
    }
  },

  // Members
  async listMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    if (shouldUseMockMembers(workspaceId)) {
      return getMockWorkspaceMembers(workspaceId);
    }

    try {
      const response = await fetch(`${API_BASE}/${workspaceId}/members`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (response.status === 404) {
        return getMockWorkspaceMembers(workspaceId);
      }
      return handleResponse<WorkspaceMember[]>(response);
    } catch (err) {
      console.warn('Workspace members API unavailable, using mock:', err);
      return getMockWorkspaceMembers(workspaceId);
    }
  },

  async addMember(workspaceId: string, data: AddMemberRequest): Promise<WorkspaceMember> {
    if (shouldUseMockMembers(workspaceId)) {
      return {
        id: `mock-member-${Date.now()}`,
        workspace_id: workspaceId,
        user_id: data.user_id,
        role: data.role ?? 'member',
        joined_at: new Date().toISOString(),
        user: {
          id: data.user_id,
          name: data.user_id,
          email: `${data.user_id}@example.com`,
        },
      };
    }

    const response = await fetch(`${API_BASE}/${workspaceId}/members`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<WorkspaceMember>(response);
  },

  async updateMemberRole(
    workspaceId: string,
    memberId: string,
    role: WorkspaceMemberRole
  ): Promise<WorkspaceMember> {
    const response = await fetch(`${API_BASE}/${workspaceId}/members/${memberId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ role }),
    });
    return handleResponse<WorkspaceMember>(response);
  },

  async removeMember(workspaceId: string, memberId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${workspaceId}/members/${memberId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Remove failed' }));
      throw new Error(getErrorMessage(error, 'Remove failed'));
    }
  },
};
